import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import {
  Users, Crown, Briefcase, Ban, Image as ImageIcon, Heart, Download,
  Wallet as WalletIcon, ArrowRight, FolderOpen,
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { getParticipants, getSubPromoters, getBusinessAssistants, getBlockedUsers } from "../../services/eventDetailService";
import { getGalleriesByEvent } from "../../services/galleryService";
import { getEventLikedMedia, getEventDownloadedMedia } from "../../services/historyService";
import { getPromoterEarnings } from "../../services/walletService";
import { extractListResult } from "../../utils/apiHelpers";
import Card from "../common/Card";
import Loader from "../common/Loader";
import EmptyState from "../common/EmptyState";
import { color, radius } from "../../styles/theme";

const totalCountOf = (data) => data?.Metadata?.TotalCount ?? data?.Metadata?.totalCount ?? extractListResult(data).length;
const money = (amount) => `${(Number(amount) || 0).toFixed(2)} €`;

const StatTile = ({ icon: Icon, label, value }) => (
  <div style={s.statTile}>
    <span style={s.statIcon}><Icon size={16} strokeWidth={2} /></span>
    <div>
      <div style={s.statValue}>{value}</div>
      <div style={s.statLabel}>{label}</div>
    </div>
  </div>
);

/**
 * Summary tab — a one-screen recap of an event's whole lifecycle: who
 * participated, how the media performed (likes/downloads), and what it
 * earned. Not a mobile port (no single mobile screen combines these) — built
 * by composing already-live endpoints already used elsewhere in this panel
 * (Participants tab, Media History, Wallet). Entry point is mainly Event
 * History, whose cards land here first via `initialTab`.
 */
const SummaryTab = ({ eventId, permissions, onGoToTab }) => {
  const { t } = useTranslation(["eventDetails", "common"]);
  const navigate = useNavigate();
  const { isSubAccount, subAccountPermissions } = useAuth();

  const canViewParticipants = permissions.canManageEvents;
  const canViewMedia = permissions.canManageGallery || permissions.canManageSingleMedia;
  const canViewWallet = !isSubAccount || Boolean(subAccountPermissions?.CanManageWallet);
  const noAccess = !canViewParticipants && !canViewMedia && !canViewWallet;
  // Business Assistant is a Public-event-only concept — see the matching
  // canUseBusinessAssistants gate in ParticipantsTab.jsx.
  const canUseBusinessAssistants =
    Boolean(permissions?.isPublicEvent) && !permissions?.isFamilyEvent && !permissions?.isARoomEvent;

  const [loadingParticipants, setLoadingParticipants] = useState(canViewParticipants);
  const [participantStats, setParticipantStats] = useState(null);
  const [loadingMedia, setLoadingMedia] = useState(canViewMedia);
  const [mediaStats, setMediaStats] = useState(null);
  const [loadingPayments, setLoadingPayments] = useState(canViewWallet);
  const [payments, setPayments] = useState(null);

  useEffect(() => {
    if (!canViewParticipants) { setLoadingParticipants(false); return; }
    let active = true;
    setLoadingParticipants(true);
    Promise.allSettled([
      getParticipants(eventId, { pageNumber: 1, pageSize: 1 }),
      getSubPromoters(eventId),
      canUseBusinessAssistants ? getBusinessAssistants(eventId, { pageNumber: 1, pageSize: 1 }) : Promise.resolve(null),
      getBlockedUsers(eventId, { pageNumber: 1, pageSize: 1 }),
    ]).then(([participantsRes, subPromotersRes, baRes, blockedRes]) => {
      if (!active) return;
      setParticipantStats({
        participants: participantsRes.status === "fulfilled" ? totalCountOf(participantsRes.value.data) : 0,
        subPromoters: subPromotersRes.status === "fulfilled" ? extractListResult(subPromotersRes.value.data).length : 0,
        assistants: canUseBusinessAssistants && baRes.status === "fulfilled" ? totalCountOf(baRes.value.data) : 0,
        blocked: blockedRes.status === "fulfilled" ? totalCountOf(blockedRes.value.data) : 0,
      });
    }).finally(() => { if (active) setLoadingParticipants(false); });
    return () => { active = false; };
  }, [eventId, canViewParticipants, canUseBusinessAssistants]);

  useEffect(() => {
    if (!canViewMedia) { setLoadingMedia(false); return; }
    let active = true;
    setLoadingMedia(true);
    Promise.allSettled([
      getGalleriesByEvent(eventId),
      getEventLikedMedia(eventId, { pageNumber: 1, pageSize: 1 }),
      getEventDownloadedMedia(eventId, { pageNumber: 1, pageSize: 1 }),
    ]).then(([galleriesRes, likedRes, downloadedRes]) => {
      if (!active) return;
      const galleries = galleriesRes.status === "fulfilled" ? extractListResult(galleriesRes.value.data) : [];
      setMediaStats({
        galleryCount: galleries.length,
        mediaCount: galleries.reduce((sum, g) => sum + (Number(g.MediaCount) || 0), 0),
        likes: likedRes.status === "fulfilled" ? likedRes.value.totalCount : 0,
        downloads: downloadedRes.status === "fulfilled" ? downloadedRes.value.totalCount : 0,
      });
    }).finally(() => { if (active) setLoadingMedia(false); });
    return () => { active = false; };
  }, [eventId, canViewMedia]);

  useEffect(() => {
    if (!canViewWallet) { setLoadingPayments(false); return; }
    let active = true;
    setLoadingPayments(true);
    getPromoterEarnings({ eventId, pageNumber: 1, pageSize: 50 })
      .then((res) => { if (active) setPayments(res.data?.Result?.[0] || null); })
      .catch(() => { if (active) toast.error(t("summaryTab.toasts.paymentsLoadFailed")); })
      .finally(() => { if (active) setLoadingPayments(false); });
    return () => { active = false; };
  }, [eventId, canViewWallet, t]);

  if (noAccess) return <EmptyState text={t("summaryTab.noAccess")} />;

  return (
    <div>
      {canViewParticipants && (
        <Card style={s.section}>
          <div style={s.sectionHeader}>
            <h3 style={s.sectionTitle}><Users size={16} strokeWidth={2.25} color={color.gray500} /> {t("summaryTab.participants.title")}</h3>
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => onGoToTab("Participants")}>
              {t("summaryTab.viewDetails")} <ArrowRight size={12} strokeWidth={2.25} />
            </button>
          </div>
          {loadingParticipants ? <Loader /> : (
            <div style={s.statsGrid}>
              <StatTile icon={Users} label={t("summaryTab.participants.total")} value={participantStats?.participants ?? 0} />
              <StatTile icon={Crown} label={t("summaryTab.participants.subPromoters")} value={participantStats?.subPromoters ?? 0} />
              {canUseBusinessAssistants && (
                <StatTile icon={Briefcase} label={t("summaryTab.participants.assistants")} value={participantStats?.assistants ?? 0} />
              )}
              <StatTile icon={Ban} label={t("summaryTab.participants.blocked")} value={participantStats?.blocked ?? 0} />
            </div>
          )}
        </Card>
      )}

      {canViewMedia && (
        <Card style={s.section}>
          <div style={s.sectionHeader}>
            <h3 style={s.sectionTitle}><ImageIcon size={16} strokeWidth={2.25} color={color.gray500} /> {t("summaryTab.media.title")}</h3>
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => onGoToTab("Media")}>
              {t("summaryTab.viewDetails")} <ArrowRight size={12} strokeWidth={2.25} />
            </button>
          </div>
          {loadingMedia ? <Loader /> : (
            <div style={s.statsGrid}>
              <StatTile icon={FolderOpen} label={t("summaryTab.media.galleries")} value={mediaStats?.galleryCount ?? 0} />
              <StatTile icon={ImageIcon} label={t("summaryTab.media.mediaCount")} value={mediaStats?.mediaCount ?? 0} />
              <StatTile icon={Heart} label={t("summaryTab.media.likes")} value={mediaStats?.likes ?? 0} />
              <StatTile icon={Download} label={t("summaryTab.media.downloads")} value={mediaStats?.downloads ?? 0} />
            </div>
          )}
        </Card>
      )}

      {canViewWallet && (
        <Card style={s.section}>
          <div style={s.sectionHeader}>
            <h3 style={s.sectionTitle}><WalletIcon size={16} strokeWidth={2.25} color={color.gray500} /> {t("summaryTab.payments.title")}</h3>
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => navigate("/wallet")}>
              {t("summaryTab.viewDetails")} <ArrowRight size={12} strokeWidth={2.25} />
            </button>
          </div>
          {loadingPayments ? <Loader /> : !payments || (payments.Galleries || []).length === 0 ? (
            <EmptyState icon={WalletIcon} text={t("summaryTab.payments.empty")} />
          ) : (
            <>
              <div style={s.statsGrid}>
                <StatTile icon={WalletIcon} label={t("summaryTab.payments.total")} value={money(payments.TotalEarnings)} />
                <StatTile icon={WalletIcon} label={t("summaryTab.payments.received")} value={money(payments.TotalReceived)} />
                <StatTile icon={WalletIcon} label={t("summaryTab.payments.receivable")} value={money(payments.TotalReceivable)} />
              </div>
              <div style={s.tableWrap}>
                <table style={s.table}>
                  <thead>
                    <tr>
                      <th style={s.th}>{t("summaryTab.payments.table.gallery")}</th>
                      <th style={s.th}>{t("summaryTab.payments.table.buyers")}</th>
                      <th style={s.th}>{t("summaryTab.payments.table.received")}</th>
                      <th style={s.th}>{t("summaryTab.payments.table.receivable")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payments.Galleries.map((g) => (
                      <tr key={g.GalleryId} style={s.tr}>
                        <td style={s.td}>{g.GalleryName}</td>
                        <td style={s.td}>{g.BuyerCount ?? 0}</td>
                        <td style={s.td}>{money(g.Received)}</td>
                        <td style={s.td}>{money(g.Receivable)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </Card>
      )}
    </div>
  );
};

const s = {
  section: { marginBottom: 20 },
  sectionHeader: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, gap: 10 },
  sectionTitle: { margin: 0, fontSize: 15, fontWeight: 700, color: color.textPrimary, display: "flex", alignItems: "center", gap: 8 },

  statsGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 14 },
  statTile: { display: "flex", alignItems: "center", gap: 12, padding: "12px 14px", background: color.gray50, borderRadius: radius.sm },
  statIcon: { width: 34, height: 34, borderRadius: radius.sm, background: color.primary50, color: color.primary600, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 },
  statValue: { fontSize: 18, fontWeight: 800, color: color.textPrimary, lineHeight: 1.1 },
  statLabel: { fontSize: 11, color: color.textMuted, marginTop: 3 },

  tableWrap: { overflowX: "auto", marginTop: 16 },
  table: { width: "100%", borderCollapse: "collapse", fontSize: 13 },
  th: {
    textAlign: "left", padding: "9px 12px", fontSize: 11, fontWeight: 700,
    color: color.textMuted, textTransform: "uppercase", letterSpacing: 0.5,
    borderBottom: `2px solid ${color.gray100}`, whiteSpace: "nowrap",
  },
  tr: {},
  td: { padding: "10px 12px", borderBottom: `1px solid ${color.gray100}`, color: color.gray700 },
};

export default SummaryTab;
